<template>
  <v-card class="pa-4">
    <h2>Received Assignments by Insurer (Days)</h2>
    <v-row dense>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedDept"
          :items="deptOptions"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedClass"
          :items="filteredClassOptions"
          item-text="subCode"
          item-value="id"
          label="Classification"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-menu
          v-model="menuDate"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateRangeText"
              label="Date Range"
              prepend-icon="mdi-calendar-range"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
              :placeholder="'Select date range'"
            />
          </template>
          <v-date-picker
            v-model="dateRange"
            range
            @change="menuDate = false"
            :max="maxDate"
          />
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          color="primary"
          @click="generateReport"
          :disabled="!dateRange[0] || !dateRange[1]"
        >
          Generate Report
        </v-btn>
        <v-btn
          color="success"
          class="ml-2"
          @click="exportExcel"
          :loading="exporting"
          :disabled="!reportData.length"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Export to Excel
        </v-btn>
      </v-col>
    </v-row>
    <v-divider class="my-4" />
    <div v-if="reportData.length">
      <div class="mb-4">
        <v-alert
          type="success"
          outlined
          prominent
          border="left"
          color="primary"
          class="text-h5 font-weight-bold"
        >
          Total Files: {{ totals.total }}
        </v-alert>
      </div>
      <v-simple-table>
        <thead>
          <tr>
            <th>Insurer</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in reportData">
            <tr :key="row.insurerCode">
              <td>
                <strong>{{ row.insurerCode }}</strong>
              </td>
              <td>{{ row.total }}</td>
            </tr>
            <tr
              v-for="detail in row.details"
              :key="row.insurerCode + '-' + detail.dept + '-' + detail.class"
              class="grey--text text--darken-2"
            >
              <td>
                <span style="padding-left: 24px">
                  {{ row.insurerCode }} ({{ detail.dept }} - {{ detail.class }})
                </span>
              </td>
              <td>{{ detail.total }}</td>
            </tr>
          </template>
          <tr>
            <td><strong>TOTAL</strong></td>
            <td>
              <strong>{{ totals.total }}</strong>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
    <v-alert v-else-if="noDataMessage" type="warning" outlined class="mt-4">
      {{ noDataMessage }}
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      Please select department, classification, and date range, then click
      "Generate Report" to view data.
    </v-alert>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import DeptDataService from "../../services/DeptDataService";
import SubDeptDataService from "../../services/SubDeptDataService";
import { apiConfig } from "../../config/api.config";

export default {
  name: "ReceivedDaysInsurer",
  data() {
    return {
      selectedDept: "all",
      deptOptions: [],
      selectedClass: "all",
      classOptions: [],
      filteredClassOptions: [],
      dateRange: [null, null],
      menuDate: false,
      reportData: [],
      totals: {
        total: 0,
      },
      exporting: false,
      noDataMessage: "",
      maxDate: moment().format("YYYY-MM-DD"),
    };
  },
  computed: {
    dateRangeText() {
      if (this.dateRange[0] && this.dateRange[1]) {
        // Use imported moment instead of this.$moment
        return (
          moment(this.dateRange[0]).format("DD MMM YYYY") +
          " - " +
          moment(this.dateRange[1]).format("DD MMM YYYY")
        );
      }
      return "";
    },
  },
  watch: {
    selectedDept(newDept) {
      this.filterClassifications(newDept);
      this.selectedClass = "all";
    },
    classOptions: {
      handler() {
        this.filterClassifications(this.selectedDept);
      },
      immediate: true,
    },
  },
  methods: {
    async loadDepartments() {
      const res = await DeptDataService.getAll();
      this.deptOptions = [
        { id: "all", name: "ALL DEPARTMENTS" },
        ...res.data,
      ].sort((a, b) => a.name.localeCompare(b.name));
    },
    async loadClassifications() {
      const res = await SubDeptDataService.getAll();
      // Always ensure ALL CLASSIFICATIONS is present and at the top
      this.classOptions = [
        { id: "all", subCode: "ALL CLASSIFICATIONS", deptId: "all" },
        ...res.data,
      ].sort((a, b) => {
        if (a.id === "all") return -1;
        if (b.id === "all") return 1;
        return a.subCode.localeCompare(b.subCode);
      });
    },
    filterClassifications(deptId) {
      // Only show ALL CLASSIFICATIONS if ALL DEPARTMENTS is selected
      if (deptId === "all") {
        this.filteredClassOptions = this.classOptions.filter(
          (c) => c.id === "all"
        );
      } else {
        // Filter for selected department, but always include ALL CLASSIFICATIONS
        const allClass = this.classOptions.find((c) => c.id === "all");
        const filtered = this.classOptions.filter((c) => c.deptId === deptId);
        this.filteredClassOptions = [allClass, ...filtered].filter(Boolean);
      }
    },
    async generateReport() {
      this.reportData = [];
      this.totals = {
        total: 0,
      };
      this.noDataMessage = "";
      try {
        const params = {
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          startDate: this.dateRange[0]
            ? moment(this.dateRange[0]).format("YYYY-MM-DD")
            : null,
          endDate: this.dateRange[1]
            ? moment(this.dateRange[1]).format("YYYY-MM-DD")
            : null,
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/received-month-insurer`,
          { params }
        );
        this.processReportData(res.data);
        if (
          !Array.isArray(res.data.reportData) ||
          res.data.reportData.length === 0
        ) {
          this.noDataMessage = `No received files found for selected date range.`;
        }
      } catch (err) {
        this.noDataMessage =
          err.message || "Failed to fetch report data. Please try again.";
      }
    },
    processReportData(data) {
      this.reportData = Array.isArray(data.reportData) ? data.reportData : [];
      let total = 0;
      this.reportData.forEach((row) => {
        total += row.total;
      });
      this.totals.total = total;
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          startDate: this.dateRange[0]
            ? moment(this.dateRange[0]).format("YYYY-MM-DD")
            : null,
          endDate: this.dateRange[1]
            ? moment(this.dateRange[1]).format("YYYY-MM-DD")
            : null,
        };
        const url =
          `${apiConfig.baseURL}/compliance/received-month-insurer/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.loadDepartments();
    this.loadClassifications();
  },
};
</script>
